import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import PageHeader from '../../components/PageHeader';
import LoadingSpinner from '../../components/LoadingSpinner';
import { useAuthStore } from '../../stores/authStore';
import { authService } from '../../services/auth';
import { clientApi } from '../../services/client';
import api from '../../services/api';
import extractApiError from '../../utils/extractApiError';
import { toGGDelta } from '../../utils/score';

interface ActivityLogItem {
  type: 'upload' | 'score_change';
  id: string;
  description: string;
  status?: string;
  user?: string;
  entity_type?: string;
  entity_id?: string;
  score_value?: number;
  final_delta?: number;
  timestamp: string;
}

function formatRelativeTime(timestamp: string): string {
  const now = new Date();
  const date = new Date(timestamp);
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  const diffWeeks = Math.floor(diffDays / 7);
  if (diffWeeks < 4) return `${diffWeeks}w ago`;
  return date.toLocaleDateString();
}

function UploadIcon() {
  return (
    <svg className="h-5 w-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
    </svg>
  );
}

function ScoreChangeIcon({ positive }: { positive: boolean }) {
  return (
    <svg
      className={`h-5 w-5 ${positive ? 'text-emerald-500' : 'text-red-500'}`}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      {positive ? (
        <path strokeLinecap="round" strokeLinejoin="round" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
      ) : (
        <path strokeLinecap="round" strokeLinejoin="round" d="M13 17h8m0 0v-8m0 8l-8-8-4 4-6-6" />
      )}
    </svg>
  );
}

export default function ClientProfile() {
  const user = useAuthStore((s) => s.user);
  const queryClient = useQueryClient();

  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [editFirstName, setEditFirstName] = useState('');
  const [editLastName, setEditLastName] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editError, setEditError] = useState<string | null>(null);

  // Stats queries
  const { data: sitesData, isLoading: sitesLoading } = useQuery({
    queryKey: ['client', 'profile', 'sites'],
    queryFn: () => clientApi.getSites({ page: '1' }).then((r) => r.data),
  });

  const { data: vendorsData, isLoading: vendorsLoading } = useQuery({
    queryKey: ['client', 'profile', 'vendors'],
    queryFn: () => clientApi.getVendors({ page: '1' }).then((r) => r.data),
  });

  // Organization name
  const { data: orgData } = useQuery({
    queryKey: ['client', 'profile', 'organization'],
    queryFn: () => clientApi.getOrganization().then((r) => r.data),
  });

  // Activity log
  const { data: activityLog, isLoading: activityLoading } = useQuery({
    queryKey: ['client', 'profile', 'activity-log'],
    queryFn: () =>
      api.get<ActivityLogItem[]>('/auth/activity-log/', { params: { limit: '15' } }).then((r) => r.data),
  });

  // Update profile mutation
  const updateMutation = useMutation({
    mutationFn: (data: { first_name: string; last_name: string; phone: string }) =>
      authService.updateMe(data),
    onSuccess: (updatedUser) => {
      useAuthStore.setState({ user: updatedUser });
      queryClient.invalidateQueries({ queryKey: ['client', 'profile'] });
      setIsEditing(false);
      setEditError(null);
    },
    onError: (err) => {
      setEditError(extractApiError(err));
    },
  });

  // Computed stats
  const sitesCount = sitesData?.count ?? 0;
  const vendorsCount = vendorsData?.count ?? 0;

  // Achievements computed from stats
  const achievements = useMemo(() => {
    const badges: { label: string; color: string }[] = [];
    if (sitesCount >= 1) badges.push({ label: 'First Site', color: 'bg-purple-100 text-purple-700' });
    if (sitesCount >= 5) badges.push({ label: '5+ Sites', color: 'bg-purple-100 text-purple-700' });
    if (sitesCount >= 20) badges.push({ label: '20+ Sites', color: 'bg-purple-100 text-purple-700' });
    if (vendorsCount >= 1) badges.push({ label: 'Vendor Connected', color: 'bg-emerald-100 text-emerald-700' });
    if (vendorsCount >= 5) badges.push({ label: '5+ Vendors', color: 'bg-emerald-100 text-emerald-700' });
    if (vendorsCount >= 10) badges.push({ label: '10+ Vendors', color: 'bg-emerald-100 text-emerald-700' });
    return badges;
  }, [sitesCount, vendorsCount]);

  const startEditing = () => {
    setEditFirstName(user?.first_name ?? '');
    setEditLastName(user?.last_name ?? '');
    setEditPhone(user?.phone ?? '');
    setEditError(null);
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setEditError(null);
  };

  const handleSave = () => {
    updateMutation.mutate({
      first_name: editFirstName,
      last_name: editLastName,
      phone: editPhone,
    });
  };

  const initials = `${user?.first_name?.[0] ?? ''}${user?.last_name?.[0] ?? ''}`.toUpperCase() || '?';
  const statsLoading = sitesLoading || vendorsLoading;

  return (
    <>
      <PageHeader title="Profile" description="Manage your account and view activity" />

      {/* Avatar / Profile Card */}
      <div className="rounded-2xl border border-slate-100 bg-white p-6 mb-8">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
          {/* Avatar */}
          <div className="flex-shrink-0 h-20 w-20 rounded-full bg-brand-100 text-brand-700 flex items-center justify-center text-2xl font-bold">
            {initials}
          </div>

          {/* Info or Edit Form */}
          <div className="flex-1 min-w-0">
            {isEditing ? (
              <div className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">First Name</label>
                    <input
                      type="text"
                      value={editFirstName}
                      onChange={(e) => setEditFirstName(e.target.value)}
                      className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 focus:border-brand-500 focus:ring-1 focus:ring-brand-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Last Name</label>
                    <input
                      type="text"
                      value={editLastName}
                      onChange={(e) => setEditLastName(e.target.value)}
                      className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 focus:border-brand-500 focus:ring-1 focus:ring-brand-500 outline-none"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Phone</label>
                  <input
                    type="tel"
                    value={editPhone}
                    onChange={(e) => setEditPhone(e.target.value)}
                    className="w-full sm:w-1/2 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-900 focus:border-brand-500 focus:ring-1 focus:ring-brand-500 outline-none"
                    placeholder="(555) 123-4567"
                  />
                </div>
                {editError && (
                  <p className="text-sm text-red-600">{editError}</p>
                )}
                <div className="flex gap-2 pt-1">
                  <button
                    onClick={handleSave}
                    disabled={updateMutation.isPending}
                    className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50 transition-colors"
                  >
                    {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
                  </button>
                  <button
                    onClick={cancelEditing}
                    disabled={updateMutation.isPending}
                    className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <>
                <h2 className="text-xl font-bold text-slate-900">
                  {user?.first_name} {user?.last_name}
                </h2>
                <p className="text-sm text-slate-500 mt-0.5">Client</p>
                <p className="text-sm text-slate-500">{user?.email}</p>
                {orgData?.name && (
                  <p className="text-sm text-slate-400 mt-1">{orgData.name}</p>
                )}
                {user?.phone && (
                  <p className="text-sm text-slate-400">{user.phone}</p>
                )}
              </>
            )}
          </div>

          {/* Edit Button */}
          {!isEditing && (
            <button
              onClick={startEditing}
              className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
            >
              Edit Profile
            </button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
        <div className="rounded-2xl border border-slate-100 bg-white p-5">
          <p className="text-sm font-medium text-slate-500">Sites Owned</p>
          {statsLoading ? (
            <div className="mt-3"><LoadingSpinner size="sm" /></div>
          ) : (
            <p className="mt-2 text-3xl font-bold text-slate-900">{sitesCount}</p>
          )}
        </div>
        <div className="rounded-2xl border border-slate-100 bg-white p-5">
          <p className="text-sm font-medium text-slate-500">Active Vendors</p>
          {statsLoading ? (
            <div className="mt-3"><LoadingSpinner size="sm" /></div>
          ) : (
            <p className="mt-2 text-3xl font-bold text-slate-900">{vendorsCount}</p>
          )}
        </div>
      </div>

      {/* Achievements */}
      <div className="rounded-2xl border border-slate-100 bg-white p-6 mb-8">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">Achievements</h2>
        {statsLoading ? (
          <div className="flex justify-center py-4"><LoadingSpinner size="sm" /></div>
        ) : achievements.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {achievements.map((badge) => (
              <span
                key={badge.label}
                className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-medium ${badge.color}`}
              >
                {badge.label}
              </span>
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-400">Complete more activities to earn achievements</p>
        )}
      </div>

      {/* Activity Timeline */}
      <div className="rounded-2xl border border-slate-100 bg-white p-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">Activity Timeline</h2>
        {activityLoading ? (
          <div className="flex justify-center py-8"><LoadingSpinner size="md" /></div>
        ) : activityLog && activityLog.length > 0 ? (
          <div className="space-y-1">
            {activityLog.map((item) => (
              <div
                key={item.id}
                className="flex items-start gap-3 rounded-xl px-3 py-3 hover:bg-slate-50 transition-colors"
              >
                {/* Icon */}
                <div className="flex-shrink-0 mt-0.5">
                  {item.type === 'upload' ? (
                    <UploadIcon />
                  ) : (
                    <ScoreChangeIcon positive={(item.final_delta ?? 0) >= 0} />
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-slate-900">{item.description}</p>
                  {item.type === 'score_change' && item.final_delta !== undefined && (
                    <span
                      className={`inline-block mt-1 text-xs font-semibold ${
                        item.final_delta >= 0 ? 'text-emerald-600' : 'text-red-600'
                      }`}
                    >
                      {item.final_delta >= 0 ? '+' : ''}{toGGDelta(item.final_delta)} pts
                    </span>
                  )}
                  {item.status && (
                    <span className="inline-block ml-2 mt-1 text-xs text-slate-400">{item.status}</span>
                  )}
                </div>

                {/* Timestamp */}
                <span className="flex-shrink-0 text-xs text-slate-400 whitespace-nowrap">
                  {formatRelativeTime(item.timestamp)}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-400 text-center py-6">No recent activity</p>
        )}
      </div>
    </>
  );
}
